import { useEffect, useState } from 'react';
import {
  createModal,
  LoadingIndicator,
  RichTable,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import { ConfigureDeviceModal, EditView } from '../manage-devices';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Link, useLocation } from 'react-router-dom';
import useAlarmsStore from 'store/alarms';
import useDevicesStore from 'store/devices';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import DevicesMap from '../devices-map';
import PanelTableRow from './panel-table-row';
import Actions from '../actions';
import { getConfigItem, isDuo } from 'util/device';
import StatusIcon from 'components/status-icon';


function BridgeView({device}) {
  const location = useLocation();
  const [alarms, setAlarms] = useState([]);
  const [detail, setDetail] = useState();
  const [status, setStatus] = useState();
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [alarmsStore] = useAlarmsStore();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [editMode, setEditMode] = useState(location.hash == '#network-settings');
  const unassigned = device.model.hasOwnProperty('assigned') && device.model.assigned === false;

  useEffect(() => {
    setEditMode(location.hash == '#network-settings');
  }, [location]);

  useEffect(() => {
    setLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
      setLoading(false);
    });

    devicesStoreActions.fetchStatus(device.model.id).then((status) => {
      setStatus(status);
    });
  }, [device]);

  useEffect(() => {
    if (!alarmsStore.alarms.length) {
      return;
    }

    // Check for alarm status on this device
    setAlarms(alarmsStore.alarms.filter(alarm => {
      if (!alarm.deviceId) return false;
      // If alarm is on this device add it
      if (alarm.deviceId == device.model.id) return true;
      // If this device is in the path of the device with the alarm add it
      const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path.map(d => d.id);
      if (!path) return false;
      return path.includes(device.model.id);
    }));
  }, [alarmsStore]);

  function editSettings() {
    setSettingsLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((res) => {
      setSettingsLoading(false);
      setDetail(res);
      const editModal = createModal(<ConfigureDeviceModal device={res} />);
      editModal.open()
        .then((res) => {
          // Handle side-effects
          devicesStoreActions.forceUpdate();
        })
        .catch(() => {/* no-op */});
    });
  }

  const columns = [
    {
      name: t`Panel`,
      key: 'name',
      sortable: true
    },
    t`Active Alarms`,
    t`Controller Count`,
    t`Circuit Count`,
    t`Actions`
  ];
  if (editMode) return (
    <EditView device={device} />
  );

  return (
    <>
    <header>
      <h2>
        {loading ?
          <LoadingIndicator /> :
          <StatusIcon device={device} alarms={alarms} commStatus={status?.commStatus} />
        }
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
          {device?.model?.deviceModel != 49 ? // Hide settings for virtual bridges
          <li>
            <button
              className={`settings ${settingsLoading ? 'processing' : ''}`}
              onClick={editSettings}
              data-tooltip-content={t`Bridge Settings`}
              data-tooltip-id="control-tooltip"
              disabled={loading || settingsLoading}
            >
              <Trans>Settings</Trans>
            </button>
          </li>
          : null}
          <li>
            <Link
              to={'#network-settings'}
              className="button network"
              data-tooltip-content={t`Network Settings`}
              data-tooltip-id="control-tooltip"
              onClick={() => setEditMode(true)}
            >
              <Trans>Network Settings</Trans>
            </Link>
          </li>
        </> : <></>}
        <Actions wrapper={true} device={device.model} />
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      <div className="overview">
        <ul>
          <li>
            <strong><Trans>Status</Trans></strong>: {unassigned ? t`Unassigned` : status?.commStatus}
            &nbsp;
          </li>
          {/* Hide firmware info from Virtual Bridges */}
          {device.model.deviceModel != 49 ? <>
          <li>
            <strong><Trans>Firmware Version</Trans></strong>:&nbsp;
            {getConfigItem(detail, 'FirmwareVersion')?.value}
          </li>
          <li>
            <strong><Trans>Firmware Updated</Trans></strong>:&nbsp;
            {new Date(getConfigItem(detail, 'FirmwareDate')?.value).toLocaleString()}
          </li>
          </> : null}
        </ul>
        <DevicesMap device={device} />
      </div>
      <RichTable
        rows={[...device.model.children]}
        columns={columns}
        responsive={true}
        items={t`panels`}
      >
        <PanelTableRow />
      </RichTable>
    </section>
    </>
  )
}

export default BridgeView;
